import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { Box, Button, MobileStepper } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";

import { PROVIDER_REGISTRATION_STEP_ORDER } from "../unauthorized/ProviderRegistration";

const Stepper = ({
  activeStep,
  setActiveStep,
  scrollRef,
}: {
  activeStep: number;
  setActiveStep: (_step: number | ((_prev: number) => number)) => void;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const { control, formState } = useFormContext();

  const fieldValues = useWatch({ control });

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);

    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({
        behavior: "instant",
        block: "start",
      });
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  let isNextDisabled = false;

  const keyOfCurrentStep = PROVIDER_REGISTRATION_STEP_ORDER[activeStep - 1]?.key as keyof typeof fieldValues;

  if (keyOfCurrentStep) {
    if (!fieldValues[keyOfCurrentStep] || formState.errors[keyOfCurrentStep]) {
      isNextDisabled = true;
    } else {
      switch (keyOfCurrentStep) {
        case "demographics": {
          isNextDisabled =
            !fieldValues[keyOfCurrentStep]?.address ||
            !Object.values(fieldValues[keyOfCurrentStep]?.genderDisclosure || {}).filter(Boolean).length ||
            !fieldValues[keyOfCurrentStep]?.languages?.length;
          break;
        }
        case "services": {
          isNextDisabled = !fieldValues[keyOfCurrentStep]?.length;
          break;
        }
        case "availability": {
          const { maxHoursWeek, generalAvailability } = fieldValues[keyOfCurrentStep] || {};
          isNextDisabled = !maxHoursWeek || !generalAvailability?.length;
          break;
        }
        case "considerations": {
          isNextDisabled = !Object.values(fieldValues[keyOfCurrentStep] || {}).filter(Boolean).length;
          break;
        }
        case "experience": {
          const { caregiverExperience, professionalCredentials } = fieldValues[keyOfCurrentStep] || {};
          isNextDisabled = caregiverExperience == null || !Object.values(professionalCredentials || {}).filter(Boolean).length;
          break;
        }
        case "agreement": {
          isNextDisabled = true;
          break;
        }
        default:
          isNextDisabled = Object.values(fieldValues[keyOfCurrentStep] || {}).some((value) => !value && value !== 0 && value !== false);
      }
    }
  }

  return (
    <MobileStepper
      activeStep={activeStep}
      backButton={
        <>
          <Box flexGrow={1} />
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            size="small"
            sx={[{ mr: 5 }, activeStep === 0 && { visibility: "hidden" }]}
          >
            <KeyboardArrowLeft />
            Back
          </Button>
        </>
      }
      nextButton={
        <>
          <Button
            disabled={isNextDisabled}
            onClick={handleNext}
            size="small"
            sx={[{ ml: 5 }, activeStep === PROVIDER_REGISTRATION_STEP_ORDER.length && { visibility: "hidden" }]}
          >
            Next
            <KeyboardArrowRight />
          </Button>
          <Box flexGrow={1} />
        </>
      }
      position="static"
      steps={10}
      sx={{
        width: "100%",
        mt: 3,
        overflow: "auto hidden",
      }}
      variant="dots"
    />
  );
};

export default Stepper;
